import * as React from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { auth, type CurrentUser, type SignupInput, ApiError } from "@/api/client";

interface AuthContextValue {
  user: CurrentUser | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<{ bootstrap?: boolean }>;
  signup: (input: SignupInput) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = React.createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const queryClient = useQueryClient();

  const meQuery = useQuery({
    queryKey: ["me"],
    queryFn: async () => {
      try {
        const { user } = await auth.me();
        return user;
      } catch (e) {
        if (e instanceof ApiError && e.status === 401) return null;
        throw e;
      }
    },
    staleTime: 60_000,
  });

  const login = React.useCallback(
    async (email: string, password: string) => {
      const { user, bootstrap } = await auth.login(email, password);
      queryClient.setQueryData(["me"], user);
      return { bootstrap };
    },
    [queryClient],
  );

  const signup = React.useCallback(
    async (input: SignupInput) => {
      const { user } = await auth.signup(input);
      queryClient.setQueryData(["me"], user);
    },
    [queryClient],
  );

  const logout = React.useCallback(async () => {
    await auth.logout();
    queryClient.setQueryData(["me"], null);
    queryClient.removeQueries();
  }, [queryClient]);

  const value: AuthContextValue = {
    user: meQuery.data ?? null,
    loading: meQuery.isLoading,
    login,
    signup,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components -- hook colocated with the provider it pairs with
export function useAuth(): AuthContextValue {
  const ctx = React.useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
